import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useAuth } from '../context/AuthContext';
import {
  ORGANIZADORES_POR_LIGA,
  OrganizadoresPorLigaData,
  OrganizadoresPorLigaVars,
  Organizador,
} from '../services/organizadorService';

export interface LigaPermissions {
  isAdministrador: boolean;
  isGerente: boolean;
  isFinanceiro: boolean;
  canEditLiga: boolean;
  canManageTournaments: boolean;
  canManagePayments: boolean;
  organizador: Organizador | null;
  isLoading: boolean;
  error: Error | null;
}

export const useLigaPermissions = (ligaId: string | null): LigaPermissions => {
  const auth = useAuth();
  const usuarioId = (auth?.user as any)?.id || null;

  const { data, loading, error } = useQuery<
    OrganizadoresPorLigaData,
    OrganizadoresPorLigaVars
  >(ORGANIZADORES_POR_LIGA, {
    variables: { ligaId: ligaId! },
    skip: !ligaId || !usuarioId,
    fetchPolicy: 'cache-and-network',
  });

  const permissions = useMemo<LigaPermissions>(() => {
    if (!ligaId || !usuarioId) {
      return {
        isAdministrador: false,
        isGerente: false,
        isFinanceiro: false,
        canEditLiga: false,
        canManageTournaments: false,
        canManagePayments: false,
        organizador: null,
        isLoading: false,
        error: null,
      };
    }

    if (loading || !data) {
      return {
        isAdministrador: false,
        isGerente: false,
        isFinanceiro: false,
        canEditLiga: false,
        canManageTournaments: false,
        canManagePayments: false,
        organizador: null,
        isLoading: true,
        error: error || null,
      };
    }

    // Encontrar o organizador do usuário atual
    const organizadorAtual = data.organizadoresPorLiga.find(
      (org) => org.usuario?.id === usuarioId
    );

    if (!organizadorAtual) {
      return {
        isAdministrador: false,
        isGerente: false,
        isFinanceiro: false,
        canEditLiga: false,
        canManageTournaments: false,
        canManagePayments: false,
        organizador: null,
        isLoading: false,
        error: null,
      };
    }

    const nomeFuncao = organizadorAtual.nomeFuncao || '';
    const isAdministrador = nomeFuncao === 'ADMINISTRADOR';
    const isGerente = nomeFuncao === 'GERENTE';
    const isFinanceiro = nomeFuncao === 'FINANCEIRO';

    return {
      isAdministrador,
      isGerente,
      isFinanceiro,
      canEditLiga: isAdministrador,
      canManageTournaments: isAdministrador || isGerente,
      canManagePayments: isAdministrador || isFinanceiro,
      organizador: organizadorAtual,
      isLoading: false,
      error: null,
    };
  }, [ligaId, usuarioId, data, loading, error]);

  return permissions;
};

